import React, { useEffect, useState } from "react";
import { Trash2, ChevronDown, ChevronRight, Briefcase, Download, MailCheck } from "lucide-react";
import { adminApi, mediaUrl } from "../../lib/api";
import { useToast } from "../../hooks/use-toast";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";

const fmt = (d) => (d ? new Date(d).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");
const notifyInbox = () => window.dispatchEvent(new Event("inbox-updated"));

const STATUSES = ["New", "Reviewing", "Interview", "Rejected"];
const STATUS_STYLE = {
  New: "bg-orange/10 text-orange border-orange/30",
  Reviewing: "bg-blue-50 text-blue-600 border-blue-200",
  Interview: "bg-violet-50 text-violet-600 border-violet-200",
  Rejected: "bg-slate-100 text-slate-500 border-slate-200",
};

export default function JobApplicationsManager() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({});
  const [deleteId, setDeleteId] = useState(null);
  const [rejectFor, setRejectFor] = useState(null); // application pending rejection confirmation

  const load = () => { setLoading(true); adminApi.jobApplications.list().then(setItems).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(load, []);

  const handleDelete = async () => {
    try { await adminApi.jobApplications.remove(deleteId); toast({ title: "Application removed" }); load(); notifyInbox(); }
    catch { toast({ title: "Error removing", variant: "destructive" }); }
    finally { setDeleteId(null); }
  };

  const applyStatus = async (a, status, sendEmail = false) => {
    setItems((list) => list.map((it) => (it.id === a.id ? { ...it, status } : it)));
    try {
      const res = await adminApi.jobApplications.setStatus(a.id, status, sendEmail);
      notifyInbox();
      if (status === "Rejected" && sendEmail) {
        toast({ title: res?.email?.sent ? "Marked rejected — email sent" : "Marked rejected (email not sent)", variant: res?.email?.sent ? undefined : "destructive" });
      } else {
        toast({ title: `Moved to ${status}` });
      }
    } catch { toast({ title: "Couldn't update", variant: "destructive" }); load(); }
  };

  const onStatusChange = (a, status) => {
    if (status === "Rejected") { setRejectFor({ ...a, _next: status }); return; }
    applyStatus(a, status, false);
  };

  const confirmReject = (sendEmail) => {
    if (rejectFor) applyStatus(rejectFor, "Rejected", sendEmail);
    setRejectFor(null);
  };

  const outstanding = items.filter((a) => (a.status || "New") === "New").length;

  return (
    <div>
      <h1 className="font-display font-extrabold text-2xl text-navy mb-1">CV Applications</h1>
      <p className="text-sm text-slate-500 mb-6">{items.length} application(s) • <span className="font-semibold text-orange">{outstanding} new</span>. Click a card to see full details, download the CV, and set a status.</p>

      {loading ? <p className="text-slate-400">Loading...</p> : items.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-100 p-12 text-center text-slate-400"><Briefcase size={36} className="mx-auto mb-3 text-slate-300" />No CV applications yet.</div>
      ) : (
        <div className="space-y-3">
          {items.map((a) => {
            const isOpen = open[a.id];
            const status = a.status || "New";
            const cvUrl = a.cv_upload_id ? mediaUrl(`/api/uploads/${a.cv_upload_id}`) : "";
            return (
              <div key={a.id} data-testid={`application-${a.id}`} className={`rounded-xl border overflow-hidden ${status === "Rejected" ? "bg-slate-50/70 border-slate-100" : "bg-white border-slate-100"}`}>
                <div className="p-4 flex items-center gap-4">
                  <button onClick={() => setOpen((o) => ({ ...o, [a.id]: !o[a.id] }))} className="text-slate-400 hover:text-navy shrink-0">{isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}</button>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-navy truncate flex items-center gap-2">
                      {a.name}
                      {a.rejection_email_sent && <span title="Rejection email sent" className="text-slate-400"><MailCheck size={14} /></span>}
                    </p>
                    <p className="text-sm text-slate-400 truncate">{a.role || "Open application"}</p>
                  </div>
                  <span className="text-xs text-slate-400 shrink-0 hidden lg:block">{fmt(a.created_at)}</span>
                  <select
                    data-testid={`application-status-${a.id}`}
                    value={status}
                    onChange={(e) => onStatusChange(a, e.target.value)}
                    className={`text-xs font-bold rounded-full border px-3 py-1.5 cursor-pointer shrink-0 outline-none ${STATUS_STYLE[status]}`}
                  >
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <button onClick={() => setDeleteId(a.id)} className="w-9 h-9 rounded-lg bg-red-50 hover:bg-red-100 flex items-center justify-center text-red-500 shrink-0"><Trash2 size={15} /></button>
                </div>
                {isOpen && (
                  <div className="border-t border-slate-100 bg-slate-50/60 p-5 space-y-4 text-sm">
                    <div className="grid sm:grid-cols-2 gap-3">
                      <Info label="Email"><a href={`mailto:${a.email}`} className="text-orange font-semibold">{a.email}</a></Info>
                      <Info label="Phone">{a.phone || "—"}</Info>
                      <Info label="Address">{a.address || "—"}</Info>
                      <Info label="Age">{a.age || "—"}</Info>
                      <Info label="Applying for">{a.role || "Open application"}</Info>
                    </div>
                    <Info label="About them">{a.message || "—"}</Info>
                    {cvUrl && (
                      <a href={cvUrl} target="_blank" rel="noopener noreferrer" data-testid={`application-download-${a.id}`} className="inline-flex items-center gap-2 bg-navy hover:bg-navy-deep text-white font-bold text-sm px-5 py-2.5 rounded-full transition-colors">
                        <Download size={15} /> Download CV{a.cv_filename ? ` (${a.cv_filename})` : ""}
                      </a>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete this application?</AlertDialogTitle><AlertDialogDescription>This will also remove the uploaded CV. This action cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600 rounded-full">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!rejectFor} onOpenChange={(o) => !o && setRejectFor(null)}>
        <AlertDialogContent data-testid="reject-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Reject {rejectFor?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Would you like to automatically send {rejectFor?.name?.split(" ")[0] || "the candidate"} the rejection email? You can edit the rejection email wording under Careers → Rejection email.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <button onClick={() => confirmReject(false)} data-testid="reject-no-email" className="inline-flex items-center justify-center rounded-full px-5 py-2 text-sm font-semibold border border-slate-200 hover:bg-slate-50 text-navy">Reject without email</button>
            <AlertDialogAction onClick={() => confirmReject(true)} data-testid="reject-send-email" className="bg-orange hover:bg-orange-dark rounded-full">Reject & send email</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const Info = ({ label, children }) => (
  <div>
    <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-0.5">{label}</p>
    <div className="text-navy leading-relaxed whitespace-pre-wrap">{children}</div>
  </div>
);
